/**
 * What the shop knows that the catalogue does not.
 *
 * `catalogue.ts` holds the measurements, and it is deliberately not touched here:
 * four of its figures are load bearing for fixtures in the consuming workbench, and
 * two of its rows are asserted whole by value, so a new field on `YarnRecord` would
 * break a test in another app for the sake of a price tag. Everything a shop needs
 * and a database does not, the colourway on the band, how many plies the spinner
 * twisted, and what a skein costs, lives in this file instead, keyed by the same
 * ids.
 *
 * ### The dye is a name, not a colour
 *
 * `dye` is a key into the blocks at the bottom of `styles/tokens.css`, not a hex
 * value. That is what keeps the whole palette in one file while still letting each
 * card draw its own colourway: a card carries `data-dye`, the block sets four
 * custom properties, and the SVG inside inherits them. `shop.test.ts` checks that
 * every dye named here has a block over there, because the failure mode otherwise
 * is a black rectangle where a swatch should be, on one card, which is exactly the
 * kind of thing nobody notices until a judge does.
 *
 * ### Money is integers
 *
 * Cents, and formatted at the edge. A price is compared and divided on this page,
 * per hundred yards, and doing that in floating point pounds and pence gets you
 * 12.989999999999998 in front of somebody eventually.
 */

import { YARNS, type YarnRecord } from './catalogue';

/** The dye families the shelf stocks. Each one is a block in `tokens.css`. */
export type DyeKey =
  | 'lichen'
  | 'madder'
  | 'indigo'
  | 'weld'
  | 'pewter'
  | 'walnut'
  | 'oatmeal';

/**
 * How a fibre behaves under the needles, which is also how it should be drawn.
 *
 * Three readings, because three is what the fibres on this shelf actually do.
 * `halo` is the animal fibres that bloom and fuzz, alpaca and mohair, so the strand
 * gets a soft edge. `sheen` is the ones with a synthetic in the blend, which catch
 * the light in a hard line. `crisp` is plain wool, which holds an edge and shows a
 * stitch cleanly, and it is the default because most of the shelf is plain wool.
 */
export type FibreCharacter = 'halo' | 'sheen' | 'crisp';

/** One row of the shelf, beside the catalogue row of the same id. */
export interface ShelfEntry {
  /** The colourway printed on the band. */
  readonly colourway: string;
  readonly dye: DyeKey;
  /** How many plies the spinner twisted together. Sets the pitch of the twist. */
  readonly plies: number;
  /** Price of one skein, in cents, so nothing is divided in floating point. */
  readonly centsPerSkein: number;
  /** Who spins it, which the catalogue name only sometimes says. */
  readonly mill: string;
}

/**
 * The shelf, keyed by catalogue id.
 *
 * The prices are the sort of thing these yarns cost, and the per hundred yard column
 * they feed tells a story that is true rather than tidy. The acrylic blend is the
 * cheapest thing here by the yard as well as by the skein, which is why people keep
 * buying it despite the short skeins, and the undyed 250 gram cone is the cheapest of
 * the pure wools, which is why whole sweaters get knitted out of it.
 */
export const SHELF: Readonly<Record<string, ShelfEntry>> = Object.freeze({
  'wota-dk': {
    colourway: 'Fern Heather',
    dye: 'lichen',
    plies: 3,
    centsPerSkein: 1299,
    mill: 'Knit Picks',
  },
  'hedgerow-dk': {
    colourway: 'Rosehip',
    dye: 'madder',
    plies: 4,
    centsPerSkein: 1650,
    mill: 'Marchmont Mill',
  },
  finch: {
    colourway: 'Bird’s Egg',
    dye: 'indigo',
    plies: 2,
    centsPerSkein: 1250,
    mill: 'Quince and Co',
  },
  chickadee: {
    colourway: 'Goldfinch',
    dye: 'weld',
    plies: 3,
    centsPerSkein: 1250,
    mill: 'Quince and Co',
  },
  'vintage-dk': {
    colourway: 'Pewter',
    dye: 'pewter',
    plies: 3,
    centsPerSkein: 625,
    mill: 'Berroco',
  },
  'cascade-220': {
    colourway: 'Walnut Heather',
    dye: 'walnut',
    plies: 4,
    centsPerSkein: 1200,
    mill: 'Cascade Yarns',
  },
  'eco-wool': {
    colourway: 'Ecru, undyed',
    dye: 'oatmeal',
    plies: 2,
    centsPerSkein: 2400,
    mill: 'Cascade Yarns',
  },
});

/**
 * The stand in for a yarn nobody has priced yet.
 *
 * A missing shelf row should not take a card off the page. Undyed, plainly plied,
 * and priced at zero, which {@link priceText} says out loud rather than printing as
 * free. `shopCoversTheCatalogue` below is what stops this being reached in practice.
 */
const UNLISTED: ShelfEntry = Object.freeze({
  colourway: 'Undyed',
  dye: 'oatmeal',
  plies: 3,
  centsPerSkein: 0,
  mill: 'Not stated',
});

/** The shelf row for a yarn, or the stand in where the shelf has no row. */
export const shelfEntry = (yarnId: string): ShelfEntry => SHELF[yarnId] ?? UNLISTED;

/** True when every yarn in the catalogue has a shelf row of its own. */
export const shopCoversTheCatalogue = (): boolean =>
  YARNS.every((yarn) => SHELF[yarn.id] !== undefined);

/**
 * How the fibre behaves, read off the words the spinner used.
 *
 * Reading a string rather than storing a flag, because the fibre content is the
 * spinner's own text and it is the thing that would actually get edited. A blend
 * that gains an alpaca should start drawing a halo without anybody remembering to
 * set a second field.
 */
export const fibreCharacter = (fibre: string): FibreCharacter => {
  const words = fibre.toLowerCase();
  if (/alpaca|mohair|angora|cashmere|yak/.test(words)) return 'halo';
  if (/acrylic|nylon|polyamide|polyester|viscose|bamboo/.test(words)) return 'sheen';
  return 'crisp';
};

/** The one line a card prints about how the fibre handles. */
export const characterText = (character: FibreCharacter): string => {
  switch (character) {
    case 'halo':
      return 'Blooms after a soak, so a cable panel closes up soft.';
    case 'sheen':
      return 'Takes a light sheen from the synthetic, and it machine washes.';
    case 'crisp':
      return 'Crisp under the needle, holds a stitch and blocks sharp.';
  }
};

// ---------- money ----------

/** Cents as a price. Whole dollars lose the trailing zeroes, as a shelf label does. */
export const priceText = (cents: number): string => {
  if (cents <= 0) return 'Price on request';
  const dollars = Math.floor(cents / 100);
  const remainder = cents % 100;
  return remainder === 0
    ? `$${dollars}`
    : `$${dollars}.${String(remainder).padStart(2, '0')}`;
};

/**
 * What a hundred yards of this yarn costs, in cents, rounded to the cent.
 *
 * The only figure on the card that compares two yarns fairly. A 50 gram skein next
 * to a 250 gram cone tells you nothing by itself, and this is the column a knitter
 * checks before deciding the cheap one is cheap.
 */
export const centsPer100Yards = (yarn: YarnRecord, entry: ShelfEntry): number =>
  yarn.yardsPerSkein <= 0
    ? 0
    : Math.round((entry.centsPerSkein / yarn.yardsPerSkein) * 100);

/** Yards per gram, to two decimals: how much length a gram of this yarn buys. */
export const yardsPerGram = (yarn: YarnRecord): number =>
  yarn.gramsPerSkein <= 0
    ? 0
    : Math.round((yarn.yardsPerSkein / yarn.gramsPerSkein) * 100) / 100;
